import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { XMarkIcon, PhotoIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { RestaurantProfile } from '../types';
import { apiService } from '../services/api';
import Cropper, { Area } from 'react-easy-crop';
import { buildRestaurantFrontendOrigin } from '../utils/restaurantUtils';
import { useToast } from '../context/ToastContext';

const RestaurantProfileComponent: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [profile, setProfile] = useState<RestaurantProfile>({
    name: '',
    id: '',
    address: '',
    description: '',
    email: '',
    hours: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: ''
    },
    logo: '',
    coverImage: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      x: ''
    },
    mapurl: '',
    plan: undefined,
    enable_table_ordering: false,
    enable_whatsapp_ordering: false
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ id?: string; description?: string; email?: string }>({});
  
  // Image upload states
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentImageType, setCurrentImageType] = useState<'logo' | 'coverImage' | null>(null);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingImagesLoading, setExistingImagesLoading] = useState(false);
  const [existingImagesError, setExistingImagesError] = useState('');
  const [deletingImage, setDeletingImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [pendingImageName, setPendingImageName] = useState<string>('');
  const [pendingImageType, setPendingImageType] = useState<'logo' | 'coverImage' | null>(null);
  const [pendingImageMime, setPendingImageMime] = useState<string>('image/jpeg');
  const [rotation, setRotation] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  const handleImageError = (url: string) => {
    setImageErrors(prev => new Set(prev).add(url));
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Load existing images when modal opens
  useEffect(() => {
    if (showImageModal) {
      loadExistingImages();
    }
  }, [showImageModal]);


  const loadProfile = async () => {
    setLoading(true);
    try {
      // First try to load from localStorage
      const storedDetails = localStorage.getItem('restaurantDetails');
      if (storedDetails) {
        try {
          const parsed = JSON.parse(storedDetails);
          // Extract FSSAI number from "FSSAI License No. XXXXXX" format
          const description = parsed.description || '';
          const fssaiNumber = description.startsWith('FSSAI License No. ') 
            ? description.replace('FSSAI License No. ', '').trim()
            : description;
          
          const profileData = {
            ...parsed,
            description: fssaiNumber,
            hours: parsed.hours || {
              monday: '',
              tuesday: '',
              wednesday: '',
              thursday: '',
              friday: '',
              saturday: '',
              sunday: ''
            },
            socialMedia: parsed.socialMedia || {
              facebook: '',
              instagram: '',
              x: ''
            }
          };
          setProfile(profileData);
          setLoading(false);
          return; // Use localStorage data, no API call needed
        } catch (error) {
          console.error('Error parsing restaurantDetails from localStorage:', error);
        }
      }

      // Only call API if localStorage is empty or parsing failed
      const response = await apiService.getRestaurantProfile();
      if (response.status && response.doc) {
        // Extract FSSAI number from "FSSAI License No. XXXXXX" format
        const description = response.doc.description || '';
        const fssaiNumber = description.startsWith('FSSAI License No. ') 
          ? description.replace('FSSAI License No. ', '').trim()
          : description;
        
        // Ensure hours and socialMedia objects are properly initialized
        const profileData = {
          ...response.doc,
          description: fssaiNumber,
          hours: response.doc.hours || {
            monday: '',
            tuesday: '',
            wednesday: '',
            thursday: '',
            friday: '',
            saturday: '',
            sunday: ''
          },
          socialMedia: response.doc.socialMedia || {
            facebook: '',
            instagram: '',
            x: ''
          }
        };
        setProfile(profileData);
        // Save restaurant details to localStorage for use in bills
        localStorage.setItem('restaurantDetails', JSON.stringify(profileData));
      } else {
        showError('Error', 'Failed to load profile');
      }
    } catch (error) {
      showError('Error', 'Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingImages = async () => {
    setExistingImagesLoading(true);
    setExistingImagesError('');
    try {
      const response = await apiService.getImages('restaurantImage');
      if (response.status && response.data) {
        setExistingImages(response.data.map(img => img.path));
      } else {
        setExistingImagesError('Failed to load images');
      }
    } catch (error) {
      setExistingImagesError('Error loading images');
    } finally {
      setExistingImagesLoading(false);
    }
  };

  const readFileAsDataURL = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, []);

  const createImage = useCallback((url: string) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });
  }, []);

  const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180;

  const rotateSize = (width: number, height: number, rotationValue: number) => {
    const rotRad = getRadianAngle(rotationValue);

    return {
      width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
  };

  const getCroppedBlob = useCallback(
    async (imageSrc: string, pixelCrop: Area, mimeType: string, rotationValue: number, asCircle: boolean) => {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to create canvas context');
      }

      const rotRad = getRadianAngle(rotationValue);
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotationValue);

      canvas.width = bBoxWidth;
      canvas.height = bBoxHeight;

      ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      ctx.rotate(rotRad);
      ctx.translate(-image.width / 2, -image.height / 2);
      ctx.drawImage(image, 0, 0);

      const croppedCanvas = document.createElement('canvas');
      const croppedCtx = croppedCanvas.getContext('2d');

      if (!croppedCtx) {
        throw new Error('Failed to create cropped canvas context');
      }

      croppedCanvas.width = pixelCrop.width;
      croppedCanvas.height = pixelCrop.height;

      if (asCircle) {
        croppedCtx.clearRect(0, 0, croppedCanvas.width, croppedCanvas.height);
        croppedCtx.save();
        croppedCtx.beginPath();
        const radius = Math.min(croppedCanvas.width, croppedCanvas.height) / 2;
        croppedCtx.arc(croppedCanvas.width / 2, croppedCanvas.height / 2, radius, 0, Math.PI * 2);
        croppedCtx.closePath();
        croppedCtx.clip();
      }

      croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );

      if (asCircle) {
        croppedCtx.restore();
      }

      return new Promise<Blob>((resolve, reject) => {
        croppedCanvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(blob);
          },
          asCircle || mimeType === 'image/png' ? 'image/png' : 'image/jpeg',
          asCircle || mimeType === 'image/png' ? undefined : 0.92
        );
      });
    },
    [createImage]
  );

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentImageType) return;

    try {
      const dataUrl = await readFileAsDataURL(file);
      setCropImageSrc(dataUrl);
      setShowCropper(true);
      setPendingImageName(file.name || `${currentImageType}.jpg`);
      setPendingImageType(currentImageType);
      setPendingImageMime(file.type || 'image/jpeg');
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setRotation(0);
    } catch (err) {
      console.error('Image preview error:', err);
      showError('Error', 'Failed to load image for cropping. Please try again.');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixelsValue: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const resetCropState = useCallback(() => {
    setShowCropper(false);
    setCropImageSrc(null);
    setCroppedAreaPixels(null);
    setPendingImageName('');
    setPendingImageType(null);
    setPendingImageMime('image/jpeg');
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setRotation(0);
  }, []);

  const handleCropCancel = () => {
    resetCropState();
  };

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !pendingImageType) {
      return;
    }

    setUploadingImage(true);
    try {
      // Visual crop shape is round for logo, but output image should be square
      const isLogoCircle = false; // Always output square images, even for logo
      const croppedBlob = await getCroppedBlob(cropImageSrc, croppedAreaPixels, pendingImageMime, rotation, isLogoCircle);
      const fileExtension = (pendingImageMime === 'image/png') ? 'png' : 'jpg';
      const safeFileName = pendingImageName || `${pendingImageType}.${fileExtension}`;
      const croppedFile = new File([croppedBlob], safeFileName, { type: croppedBlob.type });

      const result = await apiService.uploadImage(croppedFile, 'restaurantImage');

      if (result.status && result.data) {
        const updatedProfile = {
          ...profile,
          [pendingImageType]: result.data!.path
        };
        setProfile(updatedProfile);
        // Update localStorage after successful image upload
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        
        // Automatically update profile if logo is changed
        if (pendingImageType === 'logo') {
          try {
            const updateResponse = await apiService.updateRestaurantProfile(updatedProfile);
            if (updateResponse.status) {
              showSuccess('Success', 'Logo updated successfully!');
            } else {
              showError('Error', updateResponse.message || 'Failed to update logo');
            }
          } catch (error) {
            console.error('Error updating profile:', error);
            showError('Error', 'Logo uploaded but failed to update profile. Please try again.');
          }
        }
        
        await loadExistingImages();
        setShowImageModal(false);
        setCurrentImageType(null);
        resetCropState();
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      showError('Error', 'Failed to upload cropped image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageSelect = async (imageUrl: string) => {
    if (currentImageType) {
      const updatedProfile = {
        ...profile,
        [currentImageType]: imageUrl
      };
      setProfile(updatedProfile);
      // Update localStorage when image is selected
      localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
      
      // Automatically update profile if logo is changed
      if (currentImageType === 'logo') {
        try {
          const updateResponse = await apiService.updateRestaurantProfile(updatedProfile);
          if (updateResponse.status) {
            showSuccess('Success', 'Logo updated successfully!');
          } else {
            showError('Error', updateResponse.message || 'Failed to update logo');
          }
        } catch (error) {
          console.error('Error updating profile:', error);
          showError('Error', 'Failed to update logo. Please try again.');
        }
      }
    }
    setShowImageModal(false);
    setCurrentImageType(null);
  };

  const handleImageDelete = async (imageUrl: string) => {
    if (!confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      return;
    }

    setDeletingImage(imageUrl);
    try {
      const result = await apiService.deleteImage(imageUrl);
      if (result.status) {
        setExistingImages(prev => prev.filter(img => img !== imageUrl));
        // If the deleted image was being used, clear it
        let updatedProfile = { ...profile };
        if (profile.logo === imageUrl) {
          updatedProfile = { ...updatedProfile, logo: '' };
        }
        if (profile.coverImage === imageUrl) {
          updatedProfile = { ...updatedProfile, coverImage: '' };
        }
        setProfile(updatedProfile);
        // Update localStorage after image deletion
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
        showSuccess('Success', 'Image deleted successfully');
      } else {
        showError('Error', result.message || 'Failed to delete image');
      }
    } catch (error) {
      showError('Error', (error as Error)?.message || 'Error deleting image');
    } finally {
      setDeletingImage(null);
    }
  };

  const openImageModal = (imageType: 'logo' | 'coverImage') => {
    setCurrentImageType(imageType);
    setShowImageModal(true);
  };

  // Function to convert iframe HTML to Google Maps embed URL
  const convertIframeToEmbedUrl = (input: string): string => {
    // Check if input contains an iframe
    const iframeMatch = input.match(/<iframe[^>]*src=["']([^"']*)["'][^>]*>/i);
    
    if (iframeMatch && iframeMatch[1]) {
      const srcUrl = iframeMatch[1];
      
      // Check if it's a Google Maps embed URL
      if (srcUrl.includes('google.com/maps/embed')) {
        return srcUrl;
      }
    }
    
    // If no iframe found or not a Google Maps URL, return original input
    return input;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    const username = profile.id.trim();
    // 1–63 chars, lowercase letters, numbers, hyphens; cannot start/end with hyphen or be only numbers
    const usernameRegex = /^(?!-)(?!.*-$)(?![0-9]+$)[a-z0-9-]{1,63}$/;

    if (!usernameRegex.test(username)) {
      const errorMessage =
        'Shop Username can contain letters (a–z), numbers (0–9), and hyphens. It cannot start or end with a hyphen, and cannot be only numbers.';
      setFieldErrors(prev => ({ ...prev, id: errorMessage }));
      showError('Error', errorMessage);
      return;
    }

    // Validate FSSAI License Number (14 digits)
    if (profile.description) {
      const fssaiRegex = /^\d{14}$/;
      if (!fssaiRegex.test(profile.description.trim())) {
        const errorMessage = 'Enter a valid 14-digit FSSAI number';
        setFieldErrors(prev => ({ ...prev, description: errorMessage }));
        showError('Error', errorMessage);
        return;
      }
    }

    // Validate Email (required)
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!profile.email || !profile.email.trim()) {
      const errorMessage = 'Email address is required';
      setFieldErrors(prev => ({ ...prev, email: errorMessage }));
      showError('Error', errorMessage);
      return;
    }
    if (!emailRegex.test(profile.email.trim())) {
      const errorMessage = 'Please enter a valid email address';
      setFieldErrors(prev => ({ ...prev, email: errorMessage }));
      showError('Error', errorMessage);
      return;
    }

    setSaving(true);

    try {
      // Format FSSAI number with prefix before saving
      const fssaiNumber = profile.description ? profile.description.trim() : '';
      const formattedFssai = fssaiNumber ? `FSSAI License No. ${fssaiNumber}` : '';
      
      // Do not send plan key in profile update
      const profileForUpdate: any = { 
        ...profile,
        description: formattedFssai
      };
      if ('plan' in profileForUpdate) {
        delete profileForUpdate.plan;
      }

      const response = await apiService.updateRestaurantProfile(profileForUpdate);
      if (response.status) {
        // Update localStorage after successful API call
        localStorage.setItem('restaurantDetails', JSON.stringify(profile));
        showSuccess('Success', 'Profile updated successfully!');
      } else {
        const errorMessage = response.message || 'Failed to update profile';
        showError('Error', errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Error updating profile';
      showError('Error', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('hours.')) {
      const day = name.split('.')[1];
      setProfile(prev => ({
        ...prev,
        hours: {
          ...prev.hours,
          [day]: value
        }
      }));
    } else if (name.startsWith('socialMedia.')) {
      const platform = name.split('.')[1];
      setProfile(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: value
        }
      }));
    } else if (name === 'mapurl') {
      // Convert iframe HTML to embed URL if needed
      const convertedValue = convertIframeToEmbedUrl(value);
      setProfile(prev => ({
        ...prev,
        [name]: convertedValue
      }));
    } else {
      setProfile(prev => ({
        ...prev,
        [name]: name === 'id' ? value.toLowerCase().trim() : value
      }));

      if (name === 'id') {
        // Clear username error when user edits the field again
        setFieldErrors(prev => ({ ...prev, id: undefined }));
      }
      
      if (name === 'description') {
        // Clear description error when user edits the field again
        setFieldErrors(prev => ({ ...prev, description: undefined }));
      }
      
      if (name === 'email') {
        // Clear email error when user edits the field again
        setFieldErrors(prev => ({ ...prev, email: undefined }));
      }
    }
  };

  const shopPreviewUrl = useMemo(() => {
    if (!profile.id || !profile.id.trim()) {
      return '';
    }
    try {
      return buildRestaurantFrontendOrigin({ id: profile.id.trim() });
    } catch (error) {
      console.warn('Failed to build profile preview URL', error);
      return '';
    }
  }, [profile.id]);

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <h1 className="text-2xl font-bold text-gray-900">Shop Profile</h1>
        <p className="text-sm text-gray-600">Update your shop information and settings</p>
      </div> */}


      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={profile.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="Enter shop name"
                  />
                </div>

                <div>
                  <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Username *
                  </label>
                  <input
                    type="text"
                    id="id"
                    name="id"
                    value={profile.id}
                    onChange={handleInputChange}
                    required
                    pattern="^(?!-)(?!.*-$)(?![0-9]+$)[a-z0-9-]{1,63}$"
                    title="Shop Username can contain letters (a–z), numbers (0–9), and hyphens. It cannot start or end with a hyphen, and cannot be only numbers."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="Enter shop username"
                  />
                  {fieldErrors.id && (
                    <p className="mt-1 text-sm text-red-600">
                      {fieldErrors.id}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Shop Username will be automatically converted to lowercase. Allowed: letters (a–z), numbers (0–9), and hyphens, but it cannot start or end with a hyphen or be only numbers.
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    {shopPreviewUrl
                      ? (
                        <>
                          Your public store link is{' '}
                          <span className="font-semibold text-gray-900 break-all">{shopPreviewUrl}</span>
                        </>
                        )
                      : 'Enter a unique shop username to see your public store link.'}
                  </p>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profile.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="e.g., support@menunix.com"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    This email will be shared with Razorpay for receipts and other payment notifications.
                  </p>
                  {fieldErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={profile.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="123 Main Street, City, State 12345"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                FSSAI License Number *
                </label>
                <input
                  id="description"
                  name="description"
                  type='number'
                  value={profile.description}
                  onChange={handleInputChange}
                  required
                  maxLength={14}
                  pattern="\d{14}"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  placeholder="Enter 14-digit FSSAI License Number"
                />
                {fieldErrors.description && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.description}</p>
                )}
                <small className="text-gray-500">Enter 14-digit FSSAI License Number.</small>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Images</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Logo Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Logo
                  </label>
                  <div className="space-y-3">
                    {/* Image Preview */}
                    {profile.logo && (
                      <div className="relative inline-block group">
                        {imageErrors.has(profile.logo) ? (
                          <div className="w-24 h-24 flex items-center justify-center bg-gray-200 rounded-full border border-gray-300">
                            <span className="text-xs text-gray-500 text-center px-2">Image not found</span>
                          </div>
                        ) : (
                          <img
                            src={profile.logo}
                            alt="Shop Logo"
                            onError={() => handleImageError(profile.logo)}
                            className="w-24 h-24 object-cover rounded-full border border-gray-300 bg-gray-50"
                          />
                        )}
                        <button
                          type="button"
                          onClick={async () => {
                            const updatedProfile = { ...profile, logo: '' };
                            setProfile(updatedProfile);
                            localStorage.setItem('restaurantDetails', JSON.stringify(updatedProfile));
                            
                            // Automatically update profile when logo is removed
                            try {
                              const updateResponse = await apiService.updateRestaurantProfile(updatedProfile);
                              if (updateResponse.status) {
                                showSuccess('Success', 'Logo removed successfully!');
                              } else {
                                showError('Error', updateResponse.message || 'Failed to remove logo');
                              }
                            } catch (error) {
                              console.error('Error updating profile:', error);
                              showError('Error', 'Failed to remove logo. Please try again.');
                            }
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          title="Remove logo"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                       
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <button
                      type="button"
                      onClick={() => openImageModal('logo')}
                      className="flex items-center px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                      <PhotoIcon className="h-4 w-4 mr-2" />
                      {profile.logo ? 'Change Logo' : 'Upload Logo'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Operating Hours</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {daysOfWeek.map(day => (
                  <div key={day.key}>
                    <label htmlFor={`hours.${day.key}`} className="block text-sm font-medium text-gray-700 mb-2">
                      {day.label}
                    </label>
                    <input
                      type="text"
                      id={`hours.${day.key}`}
                      name={`hours.${day.key}`}
                      value={profile.hours?.[day.key as keyof typeof profile.hours] || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      placeholder="e.g., 11:00 AM - 10:00 PM"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Social Media */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Social Media</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="socialMedia.facebook" className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    id="socialMedia.facebook"
                    name="socialMedia.facebook"
                    value={profile.socialMedia?.facebook || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="https://facebook.com/xxxxxx"
                  />
                </div>

                <div>
                  <label htmlFor="socialMedia.instagram" className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    id="socialMedia.instagram"
                    name="socialMedia.instagram"
                    value={profile.socialMedia?.instagram || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="https://instagram.com/xxxxxx"
                  />
                </div>

                <div>
                  <label htmlFor="socialMedia.x" className="block text-sm font-medium text-gray-700 mb-2">
                    X (Twitter) URL
                  </label>
                  <input
                    type="url"
                    id="socialMedia.x"
                    name="socialMedia.x"
                    value={profile.socialMedia?.x || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="https://x.com/xxxxxx"
                  />
                </div>

                <div>
                  <label htmlFor="socialMedia.youtube" className="block text-sm font-medium text-gray-700 mb-2">
                    YouTube URL
                  </label>
                  <input
                    type="url"
                    id="socialMedia.youtube"
                    name="socialMedia.youtube"
                    value={profile.socialMedia?.youtube || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                    placeholder="https://youtube.com/@xxxxxx"
                  />
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Location</h2>
              
              <div>
                <label htmlFor="mapurl" className="block text-sm font-medium text-gray-700 mb-2">
                  Google Maps Embed URL
                </label>
                <textarea
                  id="mapurl"
                  name="mapurl"
                  value={profile.mapurl}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  placeholder="Paste Google Maps embed URL or iframe HTML here..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  You can paste either the embed URL or the complete iframe HTML. The system will automatically convert iframe HTML to the required format.
                  <br />
                  Get the embed URL from Google Maps → Share → Embed a map
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t">
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white font-medium rounded-lg hover:from-orange-600 hover:to-pink-700 focus:ring-2 focus:ring-pink-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Update Profile'}
              </button>
            </div>
        </form>
      </div>

      {/* Image Upload Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 !mt-0">
          <div onClick={() => setShowImageModal(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Select or Upload {currentImageType === 'logo' ? 'Logo' : 'Cover'} Image
                </h3>
                <button 
                  onClick={() => setShowImageModal(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div>
                {/* Upload Section */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Upload New Image</h4>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingImage ? (
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                        ) : (
                          <>
                            <PlusIcon className="w-8 h-8 mb-2 text-gray-500" />
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">PNG, JPG or JPEG (MAX. 5MB)</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                    </label>
                  </div>
                </div>

                {/* Existing Images Section */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Choose from Existing Images</h4>
                  
                  {existingImagesLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      <span className="ml-2 text-sm text-gray-500">Loading images...</span>
                    </div>
                  ) : existingImagesError ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{existingImagesError}</p>
                    </div>
                  ) : existingImages.length === 0 ? (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-500">No existing images found. Upload a new image to get started.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {existingImages.map((image, index) => (
                        <div
                          key={index}
                          className="relative rounded-lg overflow-hidden border-2 border-gray-300 hover:border-gray-400 cursor-pointer group"
                          onClick={() => handleImageSelect(image)}
                        >
                          {imageErrors.has(image) ? (
                            <div className="w-full h-20 flex items-center justify-center bg-gray-200">
                              <span className="text-xs text-gray-500">Image not found</span>
                            </div>
                          ) : (
                            <img
                              src={image}
                              alt={`Option ${index + 1}`}
                              onError={() => handleImageError(image)}
                              className="w-full h-20 object-cover"
                            />
                          )}
                          
                          {/* Selection indicator */}
                          {(currentImageType === 'logo' && profile.logo === image) || 
                           (currentImageType === 'coverImage' && profile.coverImage === image) ? (
                            <div className="absolute inset-0 bg-orange-500 bg-opacity-20 flex items-center justify-center">
                              <div className="bg-orange-500 text-white rounded-full p-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          ) : null}
                          
                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageDelete(image);
                            }}
                            disabled={deletingImage === image}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 group-hover:opacity-100 transition-opacity hover:bg-red-600 disabled:opacity-50"
                            title="Delete image"
                          >
                            {deletingImage === image ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              <TrashIcon className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCropper && cropImageSrc && pendingImageType && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Crop {pendingImageType === 'logo' ? 'Logo' : 'Cover'} Image
              </h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="relative w-full h-72 sm:h-96 bg-gray-900 flex-shrink-0">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape={pendingImageType === 'logo' ? 'round' : 'rect'}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                objectFit="contain"
              />
            </div>
            <div className="px-4 py-4 border-t flex-shrink-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-600 whitespace-nowrap min-w-[50px]">Zoom</span>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(event) => setZoom(Number(event.target.value))}
                      className="w-full accent-orange-600"
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-sm text-gray-600 whitespace-nowrap min-w-[70px]">Rotation</span>
                    <input
                      type="range"
                      min={-180}
                      max={180}
                      step={1}
                      value={rotation}
                      onChange={(event) => setRotation(Number(event.target.value))}
                      className="w-full accent-orange-600"
                    />
                    <span className="text-sm text-gray-800 w-12 text-right">{`${rotation}°`}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2 border-t">
                  <button
                    onClick={handleCropCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                    disabled={uploadingImage}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Uploading...' : 'Save Crop'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RestaurantProfileComponent;
